import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import classnames from 'classnames';
import format from 'date-fns/format';
import { useFeature } from 'flagged';
import propTypes from 'prop-types';
import { pick } from 'rambdax';
import CustomDialog, {
  DialogActionButton,
  DialogCancelButton,
} from '@/components/common/CustomDialog';
import ConstituentAutocomplete from '@/components/common/form/ConstituentAutocomplete';
import ControlledNotesInput from '@/components/common/form/ControlledNotesInput';
import RelationNames from '@/constants/constituents/RelationNames';
import FeatureFlags from '@/constants/system/FeatureFlags';
import { API_DATE_FORMAT } from '@/lib/constants/common';
import useConstituent from '@/lib/hooks/swr/useConstituent';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import translate from '@/lib/intl/translate';
import { ConstituentType } from '@/lib/types/ConstituentPropType';
import { ConstituentConnectionTypes } from '@/propTypes';
import AddRelationCreateConstituentForm from './AddRelationCreateConstituentForm';
import HouseholdRelationshipForm from './HouseholdRelationshipForm';
import JobRelationshipForm from './JobRelationshipForm';

const DIALOG_TITLES = {
  [RelationNames.EMPLOYERS]: 'ADD_RELATION_DIALOG_TITLE_EMPLOYER',
  [RelationNames.EMPLOYEES]: 'ADD_RELATION_DIALOG_TITLE_EMPLOYEE',
  [RelationNames.HOUSEHOLD]: 'ADD_RELATION_DIALOG_TITLE_HOUSEHOLD',
  [RelationNames.AFFILIATIONS]: 'ADD_RELATION_DIALOG_TITLE_AFFILIATION',
  [RelationNames.CONNECTIONS]: 'ADD_RELATION_DIALOG_TITLE_CONNECTION',
};

function AddRelationDialog({ constituentId, relation, onClose, open }) {
  const hasHouseholdEnhancements = useFeature(
    FeatureFlags.HOUSEHOLD_ENHANCEMENTS,
  );
  const [isBusy, setIsBusy] = React.useState(false);
  const [apiError, setApiError] = React.useState(null);

  const { createRelationship } = useConstituent(constituentId);

  const ownerOrgId = useOwnerOrgId();
  const isHouseholdRelation = relation === RelationNames.HOUSEHOLD;

  const formMethods = useForm({
    mode: 'all',
    defaultValues: {
      existingConstituent: null,
      firstName: '',
      lastName: '',
      orgName: '',
      displayName: '',
      phoneNumber: '+1',
      phoneType: null,
      emailAddress: '',
      emailType: null,
      relationLabel: null,
      relationStatus: null,
      relationshipStartDate: null,
      relationshipEndDate: null,
      ...(hasHouseholdEnhancements && { notes: '' }),
      ...(hasHouseholdEnhancements &&
        isHouseholdRelation && {
          preferredGreeting: '',
        }),
    },
  });

  const {
    clearErrors,
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
  } = formMethods;

  const existingConstituent = watch('existingConstituent');
  const hasExistingConstituent = !!existingConstituent?.constituentId;

  let constituentType = ConstituentType.PRS;
  switch (relation) {
    case RelationNames.EMPLOYERS:
    case RelationNames.AFFILIATIONS:
      constituentType = ConstituentType.ORG;
      break;
    case RelationNames.HOUSEHOLD:
    case RelationNames.EMPLOYEES:
    case RelationNames.CONNECTIONS:
      constituentType = ConstituentType.PRS;
      break;
    default:
      throw new Error(`unknown relation type: ${relation}`);
  }

  const isJobRelation = [
    RelationNames.EMPLOYERS,
    RelationNames.EMPLOYEES,
  ].includes(relation);

  const handleClearExistingConstituent = () => {
    setValue('existingConstituent', '');
  };

  const submit = React.useCallback(
    async (formData) => {
      if (Object.keys(errors).length) return;

      const relationStartDateString =
        formData.relationshipStartDate &&
        format(formData.relationshipStartDate, API_DATE_FORMAT);

      const relationEndDateString =
        formData.relationshipEndDate &&
        format(formData.relationshipEndDate, API_DATE_FORMAT);

      const relationLabel =
        formData.relationLabel?.value ?? formData.relationLabel;
      const relationStatus = formData.relationStatus?.value;

      let constituentData;

      if (!hasExistingConstituent) {
        const emails = formData.emailAddress
          ? [
              {
                email: formData.emailAddress,
                label: formData.emailType?.value,
                primary: true,
              },
            ]
          : [];

        const hasPhoneNumber =
          formData.phoneNumber && formData.phoneNumber !== '+1';

        const phones = hasPhoneNumber
          ? [
              {
                number: formData.phoneNumber,
                label: formData.phoneType?.value,
                primary: true,
              },
            ]
          : [];

        constituentData = {
          constituentType,
          ownerOrgId,
          emails,
          phones,
          ...pick(['firstName', 'orgName'], formData),
          ...(formData.lastName ? { lastName: formData.lastName } : {}),
          ...(formData.displayName
            ? { displayName: formData.displayName }
            : {}),
        };
      }

      const relationshipData = {
        ...(hasHouseholdEnhancements && {
          notes: formData.notes || '',
        }),
        ...(hasHouseholdEnhancements &&
          isHouseholdRelation && {
            preferredGreeting: formData.preferredGreeting || '',
          }),
        relationStartDateString,
        relationEndDateString,
        relationStatus,
        relationLabel,
        relatedId: hasExistingConstituent
          ? existingConstituent.constituentId
          : undefined,
      };

      setIsBusy(true);
      setApiError(null);

      try {
        await createRelationship({
          constituentId,
          constituentData,
          relationshipData,
          relationName: relation,
        });

        handleClearExistingConstituent();
        reset();
        onClose();
      } catch (e) {
        setApiError(translate('API_ERROR_UNKNOWN'));
      } finally {
        setIsBusy(false);
      }
    },
    [
      constituentType,
      errors,
      existingConstituent,
      isHouseholdRelation,
      ownerOrgId,
      relation,
    ],
  );

  const handleCancel = () => {
    reset();
    onClose();
  };

  const hasRelationInfo = isJobRelation || isHouseholdRelation;

  return (
    <CustomDialog
      dialogHeader={translate(DIALOG_TITLES[relation])}
      fullWidth
      maxWidth="lg"
      onClose={handleCancel}
      open={open}
      dialogActions={
        <>
          {apiError && (
            <div className="order-none mb-3 flex-grow-0 sm:order-1 sm:mb-0 sm:flex-grow">
              <span className="text-md font-light text-error-main">
                {apiError}
              </span>
            </div>
          )}

          <DialogActionButton
            className="w-full p-3 sm:w-auto"
            form="add-relation-form"
            showLoader={isBusy}
            type="submit"
          >
            {translate('ADD_RELATION_DIALOG_ACTION_SUBMIT')}
          </DialogActionButton>

          <DialogCancelButton
            className="mr-3 w-full p-3 sm:w-auto"
            onClick={handleCancel}
          >
            {translate('CANCEL')}
          </DialogCancelButton>
        </>
      }
    >
      <FormProvider {...formMethods}>
        <form id="add-relation-form" onSubmit={handleSubmit(submit)} noValidate>
          <h3 className="mb-2 font-medium">
            {translate('ADD_RELATION_DIALOG_SECTION_EXISTING')}
          </h3>
          <ConstituentAutocomplete
            name="existingConstituent"
            filter={{ constituentType }}
          />
          <p className="my-4 font-light">
            {translate('ADD_RELATION_DIALOG_SECTION_CONJUNCTION')}
          </p>
          {hasExistingConstituent ? (
            <div className={classnames({ 'mb-3': hasHouseholdEnhancements })}>
              <Button
                className="p-0 normal-case"
                variant="text"
                onClick={handleClearExistingConstituent}
              >
                {translate('ADD_RELATION_DIALOG_ACTION_CLEAR_SELECTED')}
              </Button>
            </div>
          ) : (
            <>
              <h3 className="mb-2 font-medium">
                {translate('ADD_RELATION_DIALOG_SECTION_CREATE')}
              </h3>
              <AddRelationCreateConstituentForm
                clearErrors={clearErrors}
                constituentType={constituentType}
                control={control}
                errors={errors}
                setError={setError}
                watch={watch}
              />
            </>
          )}
          {hasRelationInfo && (
            <>
              <hr className="my-8 border-grey-300" />
              <h3 className="mb-2 font-medium">
                {translate('ADD_RELATION_DIALOG_SECTION_INFO')}
              </h3>
            </>
          )}
          {isHouseholdRelation && (
            <HouseholdRelationshipForm control={control} />
          )}
          {isJobRelation && <JobRelationshipForm control={control} />}

          {hasHouseholdEnhancements && (
            <ControlledNotesInput
              className="mt-3 md:col-span-full"
              control={control}
              label={translate('RELATION_LABEL_NOTES')}
              name="notes"
            />
          )}
        </form>
      </FormProvider>
    </CustomDialog>
  );
}

AddRelationDialog.propTypes = {
  constituentId: propTypes.string.isRequired,
  relation: propTypes.oneOf(Object.values(ConstituentConnectionTypes))
    .isRequired,
  onClose: propTypes.func.isRequired,
  open: propTypes.bool,
};

AddRelationDialog.defaultProps = {
  open: false,
};

export default AddRelationDialog;
